import React, { useContext, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Stack,
  Badge,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  BarChart,
  ListAlt,
  WbSunny,
  NightsStay,
  Close,
  WbSunnyOutlined,
  LightMode,
  Coffee,
  NightlightRound,
  Bedtime,
  HelpOutline,
} from "@mui/icons-material";

import DropTask from "./DropTask";
import TemplateRunModal from "../PopupModal/TemplateRunModal";
import TemplateCreateModal from "../PopupModal/TemplateCreateModal";
import DynamicSelectInput from "../FormFields/CustomSelect/DynamicSelectInput";
import Loader from "../Loader/Loader";
import MyContext from "../../context/MyContext";
import { QK } from "../../base/qk";
import {
  getRuns,
  getSubLocations,
  getTemplate,
  getUnAssignedVisits,
  updateVisits,
} from "../../api/services/scheduling";

const ItemTypes = { TASK: "card" };

const Job = ({ id, data }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TASK,
    item: { id, data },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const getShiftStyles = (shiftDetails) => {
    const shiftName = shiftDetails?.shift_name || "";
    
    switch (shiftName) {
      case "Morning Call":
        return { icon: <WbSunnyOutlined sx={{ fontSize: 15, color: "#d48400" }} />, label: "Morning" };
      case "Lunch Call":
        return { icon: <LightMode sx={{ fontSize: 15, color: "#2e7d32" }} />, label: "Lunch" };
      case "Tea Call":
        return { icon: <Coffee sx={{ fontSize: 15, color: "#795548" }} />, label: "Tea" };
      case "Evening Call":
        return { icon: <NightlightRound sx={{ fontSize: 15, color: "#0277bd" }} />, label: "Evening" };
      case "Night Call":
        return { icon: <Bedtime sx={{ fontSize: 15, color: "#c62828" }} />, label: "Night" };
      default:
        return { icon: <HelpOutline sx={{ fontSize: 15, color: "#546e7a" }} />, label: "Call" };
    }
  };

  const shift = getShiftStyles(data.shift_details);

  return (
    <Paper
      ref={drag}
      elevation={1}
      sx={{
        p: 1.2,
        mb: 1.2,
        bgcolor: data.shift_details?.shift_color || "#BDBDBD", 
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        borderRadius: "6px",
        border: isDragging ? "2px dashed #666" : "1px solid rgba(0,0,0,0.05)",
        transition: "all 0.2s ease",
        "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
      }}
    >
      {/* Header Section: Shift Icon and Client Info */}
      <Stack direction="row" spacing={1} alignItems="flex-start" mb={0.5}>
        <Box sx={{ mt: 0.2 }}>{shift.icon}</Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: "12px", color: "rgba(0,0,0,0.85)", lineHeight: 1.2 }}>
            {data.client}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: "10px", fontWeight: 600, color: "rgba(0,0,0,0.6)", display: 'block' }}>
            {data.visit_name}
          </Typography>
        </Box>
      </Stack>

      {/* Footer Section: Time and Type Badge */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: "10px", 
            fontWeight: 800, 
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.5)",
            px: 0.8,
            py: 0.2,
            borderRadius: '4px',
          }}
        >
          {data.time_required_from?.substring(0, 5)} - {data.time_required_to?.substring(0, 5)}
        </Typography>

        {data.visit_type === 2 && (
          <Badge
            badgeContent="COV"
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 9,
                height: 16,
                minWidth: 30,
                position: "static",
                transform: "none",
                fontWeight: 900,
                boxShadow: "0px 1px 3px rgba(0,0,0,0.2)"
              },
            }}
          />
        )}
      </Stack>
    </Paper>
  );
};

const TaskDropArea = ({ onDrop, label, tasks }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TASK,
    drop: (item) => onDrop(item),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  return (
    <Box
      ref={drop}
      sx={{
        p: 2,
        borderRadius: "4px",
        bgcolor: isOver ? "#f0f8ff" : "#e0e0e0",
        minHeight: "100px",
        transition: "all 0.2s ease",
      }}
    >
      <Typography
        variant="subtitle2"
        textAlign="center"
        color="text.secondary"
        sx={{ mb: 2, fontWeight: "bold" }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {tasks.map((task) => (
          <Box key={task.id} sx={{ transition: "transform 0.2s ease" }}>
            <DropTask task={task} type="template" />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Template = () => {
  const [runType, setRunType] = useState(null);
  const [selectedSublocationId, setSelectedSublocationId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showRunModal, setRunShowModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { locationID } = useContext(MyContext);

  const formattedDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    weekday: "long",
  });

  // Queries (All existing functionality kept)
  const { data: subLocations } = useQuery(
    [QK.subLocations, locationID],
    () => getSubLocations(locationID),
    { enabled: !!locationID }
  );

  const { data: runs, isLoading: runLoading } = useQuery(
    [QK.runs, selectedTemplateId, runType],
    () => getRuns(selectedTemplateId, runType),
    { enabled: !!selectedTemplateId || !!runType }
  );

  const { data: templates } = useQuery(
    [QK.templates, selectedSublocationId],
    () => getTemplate(selectedSublocationId),
    { enabled: !!selectedSublocationId }
  );

  const { data: unAssignedTasks } = useQuery(
    [QK.unAssignedTasks, selectedSublocationId],
    () => getUnAssignedVisits(selectedSublocationId),
    { enabled: !!selectedSublocationId }
  );

  const handleDrop = async (task, targetRunId) => {
    try {
      const data = { is_on_right: true, run: targetRunId };
      const response = await updateVisits(task.id, data);
      if (response.status === 200) {
        queryClient.invalidateQueries([QK.runs]);
        queryClient.invalidateQueries([QK.unAssignedTasks]);
        enqueueSnackbar("Task updated successfully", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to update task", { variant: "error" });
    }
  };

  const handleFilterClick = (type) => {
    setRunType((prev) => (prev === type ? "" : type));
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 2 }}>
        {/* Navigation & Selection Bar */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack 
            direction="row" 
            spacing={1} 
            alignItems="flex-start"
          >
            <Box sx={{ minWidth: 200 }}>
              <DynamicSelectInput
                options={subLocations}
                label="Select Area"
                name="sublocation"
                selectedValue={selectedSublocationId}
                onChange={(e) => setSelectedSublocationId(e.target.value)}
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <DynamicSelectInput
                label="Choose Template"
                name="template"
                options={templates}
                selectedValue={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              />
            </Box>

            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', pt: '0px' }}>
              <IconButton
                color="primary"
                onClick={() => setShowTemplateModal(true)}
                sx={{ 
                  border: "1px solid", 
                  borderColor: "rgba(0, 0, 0, 0.23)", 
                  borderRadius: "4px",
                  height: "40px", 
                  width: "40px",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "rgba(25, 118, 210, 0.04)"
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ListAlt />}
              sx={{ height: 36, textTransform: "none" }}
            >
              Actions
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<BarChart />}
              sx={{ height: 36, textTransform: "none" }}
            >
              Reports
            </Button>
          </Stack>
        </Stack>

        {selectedTemplateId && (
          <Grid container spacing={2}>
            {/* Left Sidebar: Unassigned Tasks */}
            <Grid item xs={12} md={2}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "#f5f5f5",
                  height: "calc(100vh - 150px)",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: "bold", mb: 2, display: "block" }}>
                  UNASSIGNED JOBS
                </Typography>
                {unAssignedTasks?.map((task) => (
                  <Job key={task.id} id={task.id} data={task} />
                ))}
              </Paper>
            </Grid>

            {/* Main Section: Runs */}
            <Grid item xs={12} md={10}>
              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "4px", p: 0 }}>
                {/* Internal Toolbar */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ p: 1, borderBottom: "1px solid #e0e0e0", bgcolor: "#fff" }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      bgcolor: "#E0E0E0",
                      px: 4,
                      py: 0.5,
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    RUNS
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setRunShowModal(true)}
                      sx={{ minWidth: "auto", py: 0 }}
                    >
                      ➕
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleFilterClick("")}
                      color={runType === "" ? "primary" : "default"}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleFilterClick("am")}
                      color={runType === "am" ? "warning" : "default"}
                    >
                      <WbSunny fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleFilterClick("pm")}
                      color={runType === "pm" ? "secondary" : "default"}
                    >
                      <NightsStay fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Typography variant="body2" sx={{ ml: "auto", fontWeight: "bold" }}>
                    {formattedDate}
                  </Typography>
                </Stack>

                {/* Runs List Area */}
                <Box sx={{ p: 2, height: "calc(100vh - 210px)", overflowY: "auto" }}>
                  {runLoading ? (
                    <Loader />
                  ) : runs?.length === 0 ? (
                    <Typography variant="body2" textAlign="center" sx={{ mt: 4, color: "text.secondary" }}>
                      No Run found
                    </Typography>
                  ) : (
                    runs.map((run, index) => (
                      <Grid container key={index} spacing={0} sx={{ mb: 3, border: "1px solid #eee" }}>
                        {/* Run Info Column */}
                        <Grid item xs={12} md={2} sx={{ p: 2, bgcolor: "#fff", borderRight: "1px solid #eee" }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}
                          >
                            {run.run_type === "am" ? (
                              <WbSunny fontSize="inherit" color="warning" />
                            ) : (
                              <NightsStay fontSize="inherit" color="secondary" />
                            )}
                            {run.name}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            {run.employee_names?.length > 0 ? (
                              run.employee_names.map((name, idx) => (
                                <Typography key={idx} variant="caption" display="block" color="text.secondary">
                                  • {name}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="caption" color="error">
                                No staff assigned
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Task Drop Area Column */}
                        <Grid item xs={12} md={10}>
                          <TaskDropArea
                            onDrop={(task) => handleDrop(task, run.id)}
                            label={`Drop for ${run.name}`}
                            tasks={run.tasks || []}
                          />
                        </Grid>
                      </Grid>
                    ))
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Modals */}
      <TemplateCreateModal
        show={showTemplateModal}
        handleClose={() => setShowTemplateModal(false)}
        sublocationId={selectedSublocationId}
      />
      <TemplateRunModal
        show={showRunModal}
        handleClose={() => setRunShowModal(false)}
        templateId={selectedTemplateId}
      />
    </DndProvider>
  );
};

export default Template;